/* ============================================================
   Site-entry splash screen — Act Two orchestration, the
   falling-heart-leaf effect, and enter/exit logic.

   Act One (the black tree/wordmark intro) and its Skip-intro button
   have been removed per explicit request — the splash is cream from
   first paint (no brown/espresso hold — that flash was only ever a
   leftover of crossfading in from Act One's dark background, and has
   been removed along with it). Shortly after load, this file adds
   `.is-revealing` to `.splash`, which fades/scales the real brand mark
   in (see assets/splash.css) — then "Enter site" appears.

   The falling leaves are built the exact same way as the add-to-cart
   heart-burst in theme.js (spawnLeafBurst/spawnLeaves/createLeafEl):
   each leaf is a one-off DOM element animated with the Web Animations
   API (`el.animate([...keyframes])`) and removed once `.finished`
   resolves — no persistent rAF loop, pool, or pointer-driven "wind"
   state.

   ---------------- LEAF TUNING ----------------
   Everything about the falling leaves lives in these constants —
   change these, nothing else, to retune the effect.
   ---------------------------------------------- */
(() => {
  // Falling leaves, spawned from the brand mark's icon — small/slow/
  // sparse, a quiet accent rather than the main event.
  const LEAF_TWO = {
    SIZE_MIN: 7, SIZE_MAX: 13,
    FALL_SPEED_MIN: 16, FALL_SPEED_MAX: 30,
    SWAY_AMP_MIN: 10, SWAY_AMP_MAX: 26,
    SWAY_FREQ_MIN: 1, SWAY_FREQ_MAX: 2,
    ROT_SPEED_MIN: 10, ROT_SPEED_MAX: 40,
    BOTTOM_MARGIN: 30,
    SPAWN_MS: [900, 1900] // [min,max] gap between leaves, continuous for as long as Act Two is showing
  };

  // Orchestration timing.
  const REVEAL_ENTER_DELAY_MS = 3000; // from the reveal triggering until "Enter site" fades in —
  // matches .splash__subheading's own fade in splash.css (1.2s delay + 1.8s duration = 3s)
  // so the Enter site button only starts once the tagline has fully finished, not before
  const REVEAL_DELAY_MS = 250;        // brief pause before the brand mark starts its fade/scale-in, so it reads as an intentional entrance rather than a jump-cut

  const splash = document.querySelector('[data-splash]');
  if (!splash) return;

  const SESSION_KEY = 'tnc_splash_seen';

  // If the tiny inline script in theme.liquid's <head> already decided
  // this visitor has seen the splash this session, <html> carries
  // .splash-skip and CSS has already hidden it — nothing left to wire up.
  if (document.documentElement.classList.contains('splash-skip')) return;

  const markIcon = splash.querySelector('[data-splash-mark-icon]');
  const leavesLayerTwo = splash.querySelector('[data-splash-leaves-two]');
  const enterBtn = splash.querySelector('[data-splash-enter]');
  const focusable = [enterBtn].filter(Boolean);

  // -------- Shared heart-leaf builder (used by Act Two's leaves) --
  // Identical source to assets/heart-logo-white.svg / heart-logo-black.svg
  // — only `fill` ever differs. A real inline <path>, not a <use> pointing
  // at a <symbol> in a separate top-level <svg> — that cross-svg reference
  // is a real WebKit/Safari paint bug (element positions/animates fine but
  // never paints), so every leaf gets its own inlined copy instead.
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const rnd = (min, max) => min + Math.random() * (max - min);
  const HEART_VB = { x: 127, y: 12, w: 87, h: 105 };
  const HEART_D = 'M 160,86 L 161,88 L 161,90 L 167,95 L 166,93 L 163,91 L 162,89 L 162,86 Z M 170,81 L 167,84 L 167,86 L 169,85 L 170,83 Z M 152,80 L 157,83 L 156,81 Z M 162,77 L 160,78 L 160,82 L 162,82 Z M 156,71 L 154,72 L 151,72 L 151,76 L 156,75 Z M 171,69 L 169,70 L 167,76 L 171,72 Z M 142,66 L 142,69 L 144,69 L 146,71 L 147,76 L 146,69 Z M 166,61 L 160,68 L 159,70 L 159,74 L 165,69 L 166,67 Z M 179,62 L 182,61 L 181,59 Z M 184,58 L 186,58 L 187,56 Z M 169,56 L 170,66 L 173,66 L 172,56 Z M 156,56 L 153,62 L 155,60 L 156,58 Z M 148,56 L 146,57 L 145,59 L 146,65 L 146,60 Z M 200,54 L 197,57 L 196,59 L 199,56 Z M 184,51 L 177,55 L 181,55 L 183,54 Z M 164,51 L 162,50 L 159,52 L 159,60 L 158,62 L 152,68 L 154,68 L 156,67 L 158,62 L 165,56 Z M 143,52 L 144,50 L 147,47 L 144,49 Z M 198,46 L 196,46 L 194,48 L 193,52 L 196,49 L 198,49 Z M 182,42 L 177,47 L 176,50 L 178,49 Z M 195,36 L 194,38 L 195,43 L 196,41 Z M 179,36 L 174,38 L 171,45 L 173,45 L 175,44 L 179,40 Z M 191,33 L 194,30 L 191,31 Z M 200,28 L 205,37 L 204,50 L 207,42 L 207,40 L 205,37 L 205,35 L 204,33 Z M 193,17 L 197,20 L 204,27 L 208,33 L 210,37 L 210,47 L 208,51 L 204,55 L 203,57 L 203,59 L 198,64 L 198,67 L 196,70 L 194,72 L 191,72 L 189,74 L 177,91 L 177,93 L 175,95 L 172,103 L 173,110 L 171,113 L 167,113 L 166,111 L 165,105 L 161,98 L 157,94 L 156,92 L 157,89 L 155,87 L 152,86 L 147,81 L 144,80 L 139,75 L 137,72 L 133,63 L 131,52 L 133,49 L 133,47 L 134,45 L 144,35 L 148,33 L 153,33 L 155,35 L 155,37 L 158,38 L 156,44 L 152,43 L 149,43 L 152,44 L 152,47 L 154,47 L 156,49 L 155,51 L 163,45 L 161,44 L 161,34 L 164,30 L 166,25 L 170,21 L 172,21 L 176,18 L 179,17 L 180,16 Z';
  function createLeafEl (size) {
    const el = document.createElementNS(SVG_NS, 'svg');
    const w = size;
    const h = size * (HEART_VB.h / HEART_VB.w);
    el.setAttribute('viewBox', `${HEART_VB.x} ${HEART_VB.y} ${HEART_VB.w} ${HEART_VB.h}`);
    el.setAttribute('width', w);
    el.setAttribute('height', h);
    el.classList.add('splash__leaf');
    const body = document.createElementNS(SVG_NS, 'path');
    body.setAttribute('d', HEART_D);
    body.setAttribute('fill', 'currentColor');
    body.setAttribute('fill-rule', 'evenodd');
    el.appendChild(body);
    return el;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.documentElement.classList.add('splash-open');

  if (reducedMotion) {
    // Static fallback (already at its settled state, brand mark + Enter
    // both fully visible) — nothing to animate or spawn. Just get focus
    // to the CTA.
    enterBtn && enterBtn.focus();
  } else {
    window.setTimeout(triggerReveal, REVEAL_DELAY_MS);
  }

  if (!reducedMotion && markIcon && leavesLayerTwo) initMarkLeaves();

  // ============================================================
  // Reveal the real brand mark
  // ============================================================
  let revealed = false;
  function triggerReveal () {
    if (revealed) return;
    revealed = true;
    splash.classList.add('is-revealing');
    splash.dispatchEvent(new CustomEvent('splash:revealing'));
    window.setTimeout(() => {
      enterBtn && enterBtn.classList.add('is-visible');
      enterBtn && enterBtn.focus();
    }, REVEAL_ENTER_DELAY_MS);
  }

  // ============================================================
  // Act Two leaves — small, slow, falling from the brand mark's icon
  // ============================================================
  function initMarkLeaves () {
    // markIcon is the <g data-splash-mark-icon> wrapping just the icon
    // paths in the brand-mark <svg> (not the wordmark) — its rendered
    // rect IS the icon's own box, no cover/letterbox math needed (plain
    // static SVG, no preserveAspectRatio games like the tree).
    function spawnLeaf () {
      const iconRect = markIcon.getBoundingClientRect();
      const stageRect = leavesLayerTwo.getBoundingClientRect();
      const x = (iconRect.left - stageRect.left) + rnd(0.1, 0.9) * iconRect.width;
      const y = (iconRect.top - stageRect.top) + rnd(0.1, 0.9) * iconRect.height;
      const fallDistance = Math.max(80, (stageRect.height - y) * rnd(0.15, 0.35) + LEAF_TWO.BOTTOM_MARGIN);

      const size = rnd(LEAF_TWO.SIZE_MIN, LEAF_TWO.SIZE_MAX);
      const el = createLeafEl(size);
      el.classList.add('splash__leaf--two');
      el.style.left = x + 'px';
      el.style.top = y + 'px';
      leavesLayerTwo.appendChild(el);

      const duration = (fallDistance / rnd(LEAF_TWO.FALL_SPEED_MIN, LEAF_TWO.FALL_SPEED_MAX)) * 1000;
      const swayAmp = rnd(LEAF_TWO.SWAY_AMP_MIN, LEAF_TWO.SWAY_AMP_MAX);
      const swayDir = Math.random() < 0.5 ? 1 : -1;
      const swayFreq = rnd(LEAF_TWO.SWAY_FREQ_MIN, LEAF_TWO.SWAY_FREQ_MAX);
      const spin = rnd(LEAF_TWO.ROT_SPEED_MIN, LEAF_TWO.ROT_SPEED_MAX) * (duration / 1000) * (Math.random() < 0.5 ? 1 : -1);
      const sway = (t) => swayDir * swayAmp * Math.sin(t * Math.PI * swayFreq);

      const anim = el.animate(
        [
          { transform: 'translate(0px,0px) rotate(0deg)', opacity: 0, offset: 0 },
          { transform: `translate(${sway(0.1)}px, ${fallDistance * 0.1}px) rotate(${spin * 0.1}deg)`, opacity: 1, offset: 0.1 },
          { transform: `translate(${sway(0.5)}px, ${fallDistance * 0.5}px) rotate(${spin * 0.5}deg)`, opacity: 1, offset: 0.5 },
          { transform: `translate(${sway(0.9)}px, ${fallDistance * 0.9}px) rotate(${spin * 0.9}deg)`, opacity: 0.5, offset: 0.9 },
          { transform: `translate(${sway(1)}px, ${fallDistance}px) rotate(${spin}deg)`, opacity: 0, offset: 1 }
        ],
        { duration, easing: 'linear', fill: 'forwards' }
      );
      anim.finished.catch(() => {}).then(() => el.remove());
    }

    // Only falls once Act Two is actually showing (icon isn't visible/
    // positioned meaningfully before then) — starts on 'splash:revealing'
    // (fired by triggerReveal, both the Act-One and desktop-skip paths)
    // and runs continuously until the splash closes.
    let stopped = false;
    function scheduleNext () {
      if (stopped) return;
      spawnLeaf();
      window.setTimeout(scheduleNext, rnd(LEAF_TWO.SPAWN_MS[0], LEAF_TWO.SPAWN_MS[1]));
    }
    splash.addEventListener('splash:revealing', () => window.setTimeout(scheduleNext, 400), { once: true });
    splash.addEventListener('splash:closed', () => { stopped = true; }, { once: true });
  }

  // ============================================================
  // Exit / enter site
  // ============================================================
  let exiting = false;
  function exitSplash () {
    if (exiting) return;
    exiting = true;
    document.documentElement.classList.remove('splash-open');
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* storage blocked — fine, just replays next load */ }
    splash.dispatchEvent(new CustomEvent('splash:closed'));

    if (reducedMotion) {
      splash.classList.add('is-hidden');
      return;
    }
    splash.classList.add('is-exiting');
    splash.addEventListener('animationend', () => splash.classList.add('is-hidden'), { once: true });
  }

  enterBtn && enterBtn.addEventListener('click', exitSplash);

  document.addEventListener('keydown', (e) => {
    if (splash.classList.contains('is-hidden')) return;

    // Minimal focus trap: only the Enter button, so just wrap Tab at the ends.
    // No Escape-to-dismiss — per explicit request, only clicking "Enter site" closes the splash.
    if (e.key === 'Tab' && focusable.length) {
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
})();
